// Единый индикатор занятого места учителя: стековая полоса по типам файлов
// и легенда «чем именно занято место и сколько». Один компонент на все места
// показа (баланс учителя, карточка пользователя в админке).

import { Alert, Box, LinearProgress, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import type { StorageUsageType, TeacherStorageSummary } from '@/types/tts'
import { ROUTES } from '@/utils/constants'
import { STORAGE_WARNING_PERCENT, bytesToMb, storagePercent } from '@/utils/storage'

interface StorageUsageIndicatorProps {
  storage: TeacherStorageSummary
  /** Заголовок блока; false — без заголовка (когда его рисует родитель) */
  title?: string | false
  /** Ссылки «где этим управлять» и предупреждение о заполнении — только своему учителю */
  showLinks?: boolean
  showWarning?: boolean
}

// Цвет сегмента и раздел, где этим типом файлов управляют. Порядок сегментов
// задаёт backend (порядок объявления enum), здесь только оформление.
const TYPE_STYLE: Record<StorageUsageType, { color: string; route?: string; routeLabelKey: string }> = {
  tts: { color: 'primary.main', route: ROUTES.DICTIONARIES, routeLabelKey: 'storage.manageDictionaries' },
  upload: { color: 'info.main', route: ROUTES.DICTIONARIES, routeLabelKey: 'storage.manageDictionaries' },
  image: { color: 'success.main', routeLabelKey: 'storage.managePrograms' },
  oral_answer: { color: 'warning.main', routeLabelKey: 'storage.managePrograms' },
  chat_voice: { color: 'secondary.main', route: ROUTES.CHAT, routeLabelKey: 'storage.manageChat' },
  // Аудио заданий грузится в конструкторе задания, а он открывается из банка
  exercise_audio: { color: 'grey.600', route: ROUTES.EXERCISE_BANK, routeLabelKey: 'storage.manageExercises' },
}

const TYPE_LABEL_DEFAULTS: Record<StorageUsageType, string> = {
  tts: 'Озвучка слов',
  upload: 'Загруженные аудио',
  image: 'Картинки программ',
  oral_answer: 'Устные ответы',
  chat_voice: 'Голосовые в чате',
  exercise_audio: 'Аудио заданий',
}

const ROUTE_LABEL_DEFAULTS: Record<string, string> = {
  'storage.manageDictionaries': 'Словари',
  'storage.managePrograms': 'Программы',
  'storage.manageChat': 'Чат',
  'storage.manageExercises': 'Задания',
}

export default function StorageUsageIndicator({
  storage,
  title,
  showLinks = true,
  showWarning = true,
}: StorageUsageIndicatorProps) {
  const { t } = useTranslation()

  const usedPercent = storagePercent(storage.used_bytes, storage.limit_bytes)
  const isUnlimited = storage.limit_bytes === null
  const nearLimit = !isUnlimited && usedPercent > STORAGE_WARNING_PERCENT

  // Ширина сегмента: от лимита, а при безлимите — от занятого объёма, иначе
  // полоса всегда была бы пустой и разбивка не читалась.
  const scaleBytes = isUnlimited ? storage.used_bytes : Math.max(storage.limit_bytes ?? 0, storage.used_bytes)
  // Пустой массив по умолчанию — защита от битого ответа, а не поддержка
  // прежнего контракта: разбивку отдаёт тот же ресурс, что и объём, но
  // глобального ErrorBoundary нет, и падение уронило бы всю страницу.
  const segments = (storage.by_type ?? [])
    .filter((item) => TYPE_STYLE[item.type] !== undefined)
    .map((item) => ({
      ...item,
      percent: scaleBytes > 0 ? (item.bytes / scaleBytes) * 100 : 0,
    }))

  return (
    <Box>
      {title !== false && (
        <Typography variant="body2" color="text.secondary">
          {title ?? t('storage.title', 'Хранилище')}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" component="div">
        {isUnlimited
          ? `${t('storage.used', 'Занято {{used}} МБ', { used: bytesToMb(storage.used_bytes) })} · ${t('storage.unlimited', 'без ограничений')}`
          : t('storage.usedOf', 'Занято {{used}} из {{limit}} МБ', {
              used: bytesToMb(storage.used_bytes),
              limit: bytesToMb(storage.limit_bytes ?? 0),
            })}
      </Typography>

      {segments.length > 0 ? (
        <Box
          sx={{
            mt: 0.5,
            display: 'flex',
            height: 8,
            borderRadius: 99,
            overflow: 'hidden',
            bgcolor: 'action.hover',
          }}
        >
          {segments.map((segment) => (
            <Box key={segment.type} sx={{ width: `${segment.percent}%`, bgcolor: TYPE_STYLE[segment.type].color }} />
          ))}
        </Box>
      ) : (
        <LinearProgress
          variant="determinate"
          value={0}
          sx={{ mt: 0.5, height: 8, borderRadius: 99 }}
        />
      )}

      {segments.length > 0 ? (
        <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {segments.map((segment) => {
            const style = TYPE_STYLE[segment.type]

            return (
              <Box key={segment.type} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: style.color, flexShrink: 0 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t(`storage.type.${segment.type}`, TYPE_LABEL_DEFAULTS[segment.type])}
                </Typography>
                <Typography variant="caption" color="text.primary" sx={{ ml: 'auto' }}>
                  {t('storage.mb', '{{mb}} МБ', { mb: bytesToMb(segment.bytes) })}
                </Typography>
                {showLinks && style.route && (
                  <Typography
                    variant="caption"
                    component={RouterLink}
                    to={style.route}
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {t(style.routeLabelKey, ROUTE_LABEL_DEFAULTS[style.routeLabelKey])}
                  </Typography>
                )}
              </Box>
            )
          })}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {t('storage.empty', 'Файлов пока нет')}
        </Typography>
      )}

      {showWarning && nearLimit && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('storage.warningNearLimit', 'Место почти закончилось. Освободите место или смените тариф.')}
        </Alert>
      )}
    </Box>
  )
}
